/**
 * Normalises a link-based API list response into the shape the Redux store expects.
 *
 * Handles both old-style `{ data: { items, total, links } }` wrappers and the
 * current flat `{ items, total, links }` shape. Derives `total_pages` from the
 * `lastPage` query-param in `links.last` so callers never have to parse it.
 */
export function adaptListResponse(apiData: any) {
  let total_pages = 1;
  if (!apiData?.items && apiData?.data?.items) apiData = apiData.data;
  try {
    const lastUrl = apiData?.links?.last;
    if (lastUrl) {
      const lastPage = new URL(lastUrl).searchParams.get('lastPage');
      if (lastPage) total_pages = parseInt(lastPage, 10);
    }
  } catch {}
  return {
    items: apiData?.items ?? [],
    total: apiData?.total ?? 0,
    links: apiData?.links ?? {},
    total_pages,
  };
}

/**
 * Merges incoming payload with the params stored from the previous fetch, saves
 * the result back to Redux state, and returns the merged object.
 *
 * Use this inside DVA/Saga effects to ensure that side-effect refreshes
 * (after create, delete, enable, etc.) automatically inherit the current
 * page, page_size, and search without the caller having to re-supply them.
 *
 * @param effects  - The `{ select, put }` object provided by DVA to the effect.
 * @param selector - Selects the stored params slice from Redux state.
 * @param saveType - The reducer action type that persists the merged params.
 * @param payload  - The new payload from the caller. Keys here win over stored ones.
 *
 * @example
 * const params = yield* mergeAndSaveListParams(
 *   { select, put },
 *   (state) => state.user.listParams,
 *   'savedListParams',
 *   payload,
 * );
 */
export function* mergeAndSaveListParams(
  effects: { select: any; put: any },
  selector: (state: any) => Record<string, any>,
  saveType: string,
  payload: Record<string, any>,
): Generator<any, Record<string, any>, any> {
  const stored: Record<string, any> = yield effects.select(selector);
  const merged = { ...stored, ...payload };

  // When page_size changes, the stored page may no longer be valid (e.g. page 5
  // with size 10 becomes out of range when switching to size 50). Reset to
  // page 1 on a page_size change so the caller never needs to remember this.
  // Only when a size was stored before: on the first pagination interaction the
  // stored params are empty, and treating that as a "change" would overwrite
  // the requested page with 1 (first click on page 2 appearing to do nothing).
  if (
    payload.page_size !== undefined &&
    stored.page_size !== undefined &&
    payload.page_size !== stored.page_size
  ) {
    merged.page = 1;
  }

  yield effects.put({ type: saveType, payload: merged });
  return merged;
}
